import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Divider, Grid, CircularProgress } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import CustomButton from "../../../components/common/CustomButton";
import CustomTextField from "../../../components/common/CustomTextField";
import CustomAutocomplete from '../../../components/common/CustomAutoComplete';
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import CustomPrefixSuffixField from '../../../components/common/CustomPrefixSuffixField';
import CustomStyle from "../../../components/common/CustomStyle";
import { ENQUIRY_SUBSCRIPTION_PLAN_VIEW, SUBSCRIPTION_LIST_PLAN_UPDATE } from '../../../services/apiRoutes';
import { getCurrencyList } from "../../../services/CommonServices";

// VALIDATION
const validationSchema = Yup.object({
    perMonthAmount: Yup.string().required("Month Amount is required"),
    planActiveDate: Yup.string().required("Plan Active Date is required"),
    planExpiredDate: Yup.string().required("Plan Expired Date is required"),
    baseAmount: Yup.string().required("Base Amount is required"),
    companyPerMonth: Yup.string().required("Company Per Month is required"),
    currency: Yup.string().required("Currency is required"),
    plan: Yup.string().required("Plan is required"),
});

const UpdateSubscriptionPlan = ({
    subscriptionPlanId,
    handleClose,
    fetchData,
    setMessage,
}) => {

    const [currencyList, setCurrencyList] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [prefix, setPrefix] = useState("");

    const planList = [
        { id: 1, title: "International" },
        { id: 2, title: "China" },
    ];

    // Load Currency List
    const loadCurrencyList = async () => {
        const d = await getCurrencyList();
        setCurrencyList(d);
    };

    // Fetch VIEW API
    useEffect(() => {
        if (!subscriptionPlanId) return;

        loadCurrencyList();

        const fetchPlan = async () => {
            try {
                const accessToken = sessionStorage.getItem("accessToken");

                const response = await axios.get(
                    ENQUIRY_SUBSCRIPTION_PLAN_VIEW(subscriptionPlanId),
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );

                const res = response?.data?.SubscriptionPlan;
                setData(res);

                // Prefix Based on Currency
                setPrefix(res?.currencyList?.currency_symbol || "");

            } catch (err) {
                console.error("Error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchPlan();
    }, [subscriptionPlanId]);

    if (loading) return <p>Loading...</p>;
    if (!data) return <p>No data found</p>;

    // SUBMIT UPDATE
    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const accessToken = sessionStorage.getItem("accessToken");

            const response = await axios.put(
                SUBSCRIPTION_LIST_PLAN_UPDATE(subscriptionPlanId),
                values,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "Updated successfully",
                    type: "success",
                });

                fetchData();
                handleClose();
            }
        } catch (err) {
            setMessage({
                open: true,
                text: "Something went wrong",
                type: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={{
                    perMonthAmount: data?.perMonthAmount || '',
                    planActiveDate: data?.planActiveDate || '',
                    planExpiredDate: data?.planExpiredDate || '',
                    baseAmount: data?.baseAmount || '',
                    companyPerMonth: data?.companyPerMonth || '',
                    currency: parseInt(data?.currency ?? ''),
                    plan: data?.plan || '',
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, handleChange, setFieldValue, values, errors, touched }) => (
                    <Form>

                        {/* HEADER */}
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={6}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    Update Subscription Plan
                                </Typography>
                            </Grid>
                            <Grid item xs={6} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider />

                        <Grid
                            container
                            spacing={1}
                            style={{ paddingLeft: "5%", paddingRight: "5%", paddingTop: "5px" }}
                        >
                            <Grid item xs={12} sm={12} md={6}>
                                <CustomAutocomplete
                                    options={planList}
                                    fieldLabel="Plan"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={planList.find((option) => option.id === values.plan) || null}
                                    onChange={(e, newValue) => {
                                        const planId = newValue?.id || '';
                                        setFieldValue('plan', planId);
                                        if (planId === 1) {
                                            setPrefix('$');
                                            setFieldValue('currency', 233); // USD
                                        } else if (planId === 2) {
                                            setPrefix('¥');
                                            setFieldValue('currency', 45); // CNY
                                        } else {
                                            setPrefix('');
                                            setFieldValue('currency', '');
                                        }
                                        setFieldValue('baseAmount', '');
                                        setFieldValue('perMonthAmount', '');
                                        setFieldValue('companyPerMonth', '');
                                    }}
                                    placeholder="Select Plan"
                                    error={touched.plan && !!errors.plan}
                                    helperText={touched.plan && errors.plan}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <CustomAutocomplete
                                    options={currencyList}
                                    fieldLabel="Currency"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={currencyList.find((option) => option.id === values.currency) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('currency', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Currency"
                                    error={touched.currency && !!errors.currency}
                                    helperText={touched.currency && errors.currency}
                                    readOnly={true}
                                    sx={CustomStyle.readOnly}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={prefix}
                                    tagLine="Includes 2 users and 1 company free"
                                    name="baseAmount"
                                    fieldLabel="Basic Subcription"
                                    value={values.baseAmount}
                                    onChange={handleChange}
                                    required={true}
                                    type="number"
                                    placeholder="Enter base Amount"
                                    error={touched.baseAmount && Boolean(errors.baseAmount)}
                                    helperText={touched.baseAmount && errors.baseAmount}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={prefix}
                                    name="perMonthAmount"
                                    fieldLabel="Additional User"
                                    tagLine="per month"
                                    value={values.perMonthAmount}
                                    onChange={handleChange}
                                    required={true}
                                    type="number"
                                    placeholder="Enter Per Month Amount"
                                    error={touched.perMonthAmount && Boolean(errors.perMonthAmount)}
                                    helperText={touched.perMonthAmount && errors.perMonthAmount}
                                />
                            </Grid>


                            <Grid item xs={12} sm={6} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={prefix}
                                    name="companyPerMonth"
                                    fieldLabel="Additional Company"
                                    tagLine="per month"
                                    value={values.companyPerMonth}
                                    onChange={handleChange}
                                    required={true}
                                    type="number"
                                    placeholder="Enter Company Per Month"
                                    error={touched.companyPerMonth && Boolean(errors.companyPerMonth)}
                                    helperText={touched.companyPerMonth && errors.companyPerMonth}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <CustomTextField
                                    type="date"
                                    name="planActiveDate"
                                    fieldLabel="Plan Start"
                                    value={values.planActiveDate}
                                    onChange={handleChange}
                                    placeholder="Enter financialYearEnd"
                                    error={touched.planActiveDate && !!errors.planActiveDate}
                                    helperText={touched.planActiveDate && errors.planActiveDate}
                                    required={true}

                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <CustomTextField
                                    type="date"
                                    name="planExpiredDate"
                                    fieldLabel="Plan expired"
                                    value={values.planExpiredDate}
                                    onChange={handleChange}
                                    placeholder="Enter financialYearEnd"
                                    error={touched.planExpiredDate && !!errors.planExpiredDate}
                                    helperText={touched.planExpiredDate && errors.planExpiredDate}
                                    required={true}
                                />
                            </Grid>

                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UpdateSubscriptionPlan;
